// Thin fetch wrapper that attaches the bearer token and unwraps the
// `{ data, meta, errors }` response envelope. The API client that WEB-02
// generates from api/openapi.yaml will replace this by-hand helper.

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly field?: string

  constructor(status: number, code: string, message: string, field?: string) {
    super(message)
    this.status = status
    this.code = code
    this.field = field
  }
}

type Envelope<T> = {
  data: T | null
  meta?: { cursor?: string; count?: number; total?: number; offset?: number; limit?: number }
  errors?: Array<{ code: string; message: string; field?: string }>
}

export type TokenGetter = () => string | null

/**
 * Fired once when the server rejects an authed request with 401 (token
 * missing / expired / revoked). The AuthProvider wires this to logout()
 * — clearing sessionStorage and setting user=null, which flips
 * <RequireAuth> and bounces the caller to /login.
 *
 * Only invoked on requests where a token WAS attached (authed !== false)
 * — a 401 on /auth/login is a bad-credentials response, not a session
 * expiry, and must not tear the session down.
 */
export type UnauthorizedHandler = () => void

type RequestOpts = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  authed?: boolean // defaults to true — explicit `false` for /auth/login etc.
}

export type ApiMeta = {
  cursor?: string
  count?: number
  total?: number
  offset?: number
  limit?: number
}

export type ApiPage<T> = { data: T; meta?: ApiMeta }

export function makeClient(getToken: TokenGetter, onUnauthorized?: UnauthorizedHandler) {
  /**
   * Central 401 handling. Fires the unauthorized handler exactly once per
   * expired-token event so the UI can log out + redirect. Only triggers
   * on authed requests that DID carry a token — a 401 on login means the
   * password was wrong, not that the session expired.
   */
  function handle401IfRelevant(status: number, opts: RequestOpts, hadToken: boolean) {
    if (status !== 401) return
    if (opts.authed === false) return
    if (!hadToken) return
    onUnauthorized?.()
  }

  async function callEnvelope<T>(path: string, opts: RequestOpts = {}): Promise<ApiPage<T>> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }
    let hadToken = false
    if (opts.authed !== false) {
      const tok = getToken()
      if (tok) {
        headers.Authorization = `Bearer ${tok}`
        hadToken = true
      }
    }
    const res = await fetch(path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })
    let payload: Envelope<T> | null = null
    const text = await res.text()
    if (text) {
      try {
        payload = JSON.parse(text) as Envelope<T>
      } catch {
        handle401IfRelevant(res.status, opts, hadToken)
        throw new ApiError(res.status, 'parse_error', text.slice(0, 200))
      }
    }
    if (!res.ok) {
      handle401IfRelevant(res.status, opts, hadToken)
      const first = payload?.errors?.[0]
      throw new ApiError(
        res.status,
        first?.code ?? 'http_error',
        first?.message ?? res.statusText,
        first?.field,
      )
    }
    if (!payload) {
      throw new ApiError(res.status, 'empty_response', 'Empty response body')
    }
    return { data: payload.data as T, meta: payload.meta }
  }

  async function call<T>(path: string, opts: RequestOpts = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }
    let hadToken = false
    if (opts.authed !== false) {
      const tok = getToken()
      if (tok) {
        headers.Authorization = `Bearer ${tok}`
        hadToken = true
      }
    }

    const res = await fetch(path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })

    // Parse defensively — health probes + errors both live in JSON.
    let payload: Envelope<T> | null = null
    const text = await res.text()
    if (text) {
      try {
        payload = JSON.parse(text) as Envelope<T>
      } catch {
        handle401IfRelevant(res.status, opts, hadToken)
        throw new ApiError(res.status, 'parse_error', text.slice(0, 200))
      }
    }

    if (!res.ok) {
      handle401IfRelevant(res.status, opts, hadToken)
      const first = payload?.errors?.[0]
      throw new ApiError(
        res.status,
        first?.code ?? 'http_error',
        first?.message ?? res.statusText,
        first?.field,
      )
    }
    if (!payload) {
      throw new ApiError(res.status, 'empty_response', 'Empty response body')
    }
    return payload.data as T
  }

  return { call, callEnvelope }
}
